import { Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription, interval, Subject } from 'rxjs';
import { switchMap, takeUntil } from 'rxjs/operators';
import { AuthService } from '../core/services/auth.service';
import { FindOneUserResponse } from '../core/model/server/vo/GetUserResponse.interface';
import { ApiService } from './api.service';
import { RbacService } from './rbac.service';

@Injectable({ providedIn: 'root' })
export class UserStatusCheckerService {
  private pollSub: Subscription | null = null;
  private cancelCheck$ = new Subject<void>();
  private readonly POLL_INTERVAL_MS = 5_000; // 5 seconds

  constructor(
    private apiService: ApiService,
    private authService: AuthService,
    private router: Router,
    private ngZone: NgZone,
    private rbacService: RbacService,
  ) {
    window.addEventListener('storage', (event: StorageEvent) => {
      if (event.key === 'sessionId' && event.newValue === null) {
        this.stopPolling();
      }
    });
  }

  checkStatus(): Promise<boolean> {
    if (!this.authService.isLoggedIn()) {
      return Promise.resolve(true);
    }

    return new Promise((resolve) => {
      this.apiService.getUserDetails().pipe(takeUntil(this.cancelCheck$)).subscribe({
        next: (response: FindOneUserResponse) => {
          if (response?.result?.status === 1) {
            this.forceLogout();
            resolve(false);
            return;
          }
          this.checkForRoleChange(response);
          resolve(true);
        },
        error: () => {
          resolve(true);
        }
      });
    });
  }

  startPolling(): void {
    if (this.pollSub) { return; }

    this.ngZone.runOutsideAngular(() => {
      this.pollSub = interval(this.POLL_INTERVAL_MS)
        .pipe(switchMap(() => this.apiService.getUserDetails()))
        .subscribe({
          next: (response: FindOneUserResponse) => {
            if (!this.authService.isLoggedIn()) {
              this.ngZone.run(() => this.stopPolling());
              return;
            }
            if (response?.result?.status === 1) { // 1 indicates inactive user
              this.ngZone.run(() => this.forceLogout());
              return;
            }
            this.ngZone.run(() => this.checkForRoleChange(response));
          },
          error: () => {}
        });
    });
  }

  stopPolling(): void {
    this.cancelCheck$.next();
    this.pollSub?.unsubscribe();
    this.pollSub = null;
  }

  private checkForRoleChange(response: FindOneUserResponse): void {
    const latestAccountType = response?.result?.accountType;
    const cachedAccountType = localStorage.getItem('accountType');

    if (latestAccountType && latestAccountType !== cachedAccountType) {
      localStorage.setItem('accountType', latestAccountType);
      window.location.reload();
    }
  }

  private forceLogout(): void {
    this.stopPolling();
    this.authService.clearSession();
    this.rbacService.reset();
    this.router.navigate(['/login']);
  }
}
